import React, { useEffect, useState, useRef } from 'react';
import {
  Box, Button, Flex, Heading, Table, Thead, Tbody, Tr, Th, Td, useToast, Spinner,
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalFooter, ModalBody, ModalCloseButton,
  FormControl, FormLabel, Input, Select, useDisclosure, Container, Card, CardBody,
  Badge, HStack, VStack, Text, Alert, AlertIcon, Center, InputGroup, InputRightElement,
  IconButton
} from '@chakra-ui/react';
import { EditIcon, DeleteIcon, ViewIcon, ViewOffIcon, LockIcon } from '@chakra-ui/icons';
import { FiRefreshCw, FiUserPlus } from 'react-icons/fi';
import AdminPasswordResetModal from '../Common/AdminPasswordResetModal';

const API_BASE_URL =
  process.env.NODE_ENV === 'production'
    ? process.env.REACT_APP_VERCEL_API_URL
    : process.env.REACT_APP_LOCAL_API_URL;

const UserList = () => {
  // Theme colors - matching AppThemeColors.js
  const themeColor = "#4bedffff";
  const themeTextColor = "black";
  const bgCard = "#fff8e1";

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editUser, setEditUser] = useState(null);
  const [newUser, setNewUser] = useState({
    username: '',
    email: '',
    password: '',
    permissions: 'Suporte',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [changePassword, setChangePassword] = useState(false);
  const [newPassword, setNewPassword] = useState('');

  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { 
    isOpen: isCreateOpen, 
    onOpen: onCreateOpen, 
    onClose: onCreateClose 
  } = useDisclosure();
  
  // Confirmation modal for delete
  const { 
    isOpen: isDeleteConfirmOpen, 
    onOpen: onDeleteConfirmOpen, 
    onClose: onDeleteConfirmClose 
  } = useDisclosure();
  const [userToDelete, setUserToDelete] = useState(null);
  const [isPasswordResetOpen, setIsPasswordResetOpen] = useState(false);
  const [userToResetPassword, setUserToResetPassword] = useState(null);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('userToken_TOVIAPPS');
      const res = await fetch(`${API_BASE_URL}api/webuser/`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
      });

      if (!res.ok) throw new Error('Falha ao buscar usuários');

      const data = await res.json();
      setUsers(data);
    } catch (err) {
      toast({
        title: 'Erro',
        description: err.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'top-right',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = (user) => {
    setUserToDelete(user);
    onDeleteConfirmOpen();
  };

  const handlePasswordReset = (user) => {
    setUserToResetPassword(user);
    setIsPasswordResetOpen(true);
  };

  const deleteUser = async () => {
    if (!userToDelete) return;
    
    setLoading(true);
    try {
      const token = localStorage.getItem('userToken_TOVIAPPS');
      const res = await fetch(`${API_BASE_URL}api/webuser/${userToDelete._id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
      });

      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.message || 'Falha ao excluir usuário');
      }

      toast({
        title: 'Sucesso',
        description: 'Usuário excluído com sucesso',
        status: 'success',
        duration: 3000,
        isClosable: true,
        position: 'top-right',
      });

      fetchUsers();
    } catch (err) {
      toast({
        title: 'Erro',
        description: err.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'top-right',
      });
    } finally {
      setLoading(false);
      onDeleteConfirmClose();
      setUserToDelete(null);
    }
  };

  const handleEditClick = (user) => {
    setEditUser(user);
    setChangePassword(false);
    setNewPassword('');
    onOpen();
  };

  const handleUpdate = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('userToken_TOVIAPPS');
      
      // Create payload with or without password
      const payload = {
        username: editUser.username,
        email: editUser.email,
        permissions: editUser.permissions,
      };
      
      // Add password to payload if requested
      if (changePassword && newPassword) {
        payload.password = newPassword;
      }
      
      const res = await fetch(`${API_BASE_URL}api/webuser/${editUser._id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.message || 'Falha ao atualizar usuário');
      }

      toast({
        title: 'Sucesso',
        description: 'Usuário atualizado com sucesso',
        status: 'success',
        duration: 3000,
        isClosable: true,
        position: 'top-right',
      });

      onClose();
      setChangePassword(false);
      setNewPassword('');
      fetchUsers();
    } catch (err) {
      toast({
        title: 'Erro',
        description: err.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'top-right',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = async () => {
    // Validate inputs
    if (!newUser.username || !newUser.email || !newUser.password) {
      toast({
        title: 'Campos obrigatórios',
        description: 'Preencha todos os campos obrigatórios.',
        status: 'warning',
        duration: 3000,
        isClosable: true,
        position: 'top-right',
      });
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('userToken_TOVIAPPS');
      const res = await fetch(`${API_BASE_URL}api/webuser/register`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          username: newUser.username,
          email: newUser.email,
          password: newUser.password,
          permissions: [newUser.permissions], // Send as array as per the backend model
        }),
      });

      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.message || 'Falha ao criar usuário');
      }

      toast({
        title: 'Sucesso',
        description: 'Usuário criado com sucesso!',
        status: 'success',
        duration: 3000,
        isClosable: true,
        position: 'top-right',
      });

      // Reset form and close modal
      setNewUser({
        username: '',
        email: '',
        password: '',
        permissions: 'Suporte',
      });
      onCreateClose();
      
      // Refresh the users list
      fetchUsers();
    } catch (err) {
      toast({
        title: 'Erro',
        description: err.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'top-right',
      });
    } finally {
      setLoading(false);
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const hasFetched = useRef(false);
  useEffect(() => {
    if (hasFetched.current) return;
    hasFetched.current = true;
    fetchUsers();
  }, []);

  return (
    <Container maxW="container.xl" p={6}>
      {/* Header Card */}
      <Card mb={6} shadow="md" borderRadius="lg" overflow="hidden" bg={bgCard}>
        <CardBody>
          <Flex justify="space-between" align="center" wrap="wrap">
            <HStack>
              <Heading as="h1" fontSize="2xl" fontWeight="bold" color="black">
                Gerenciamento de Usuários
              </Heading>
              <Badge bg={themeColor} color={themeTextColor} borderRadius="full" px={3} py={1}>
                {users.length} usuário{users.length !== 1 ? 's' : ''}
              </Badge>
            </HStack>
            
            <HStack spacing={3}>
              <Button
                leftIcon={<FiRefreshCw />}
                size="sm"
                variant="outline"
                borderColor={themeColor}
                color="black"
                onClick={fetchUsers}
                isLoading={loading}
              >
                Atualizar
              </Button>
              
              <Button
                leftIcon={<FiUserPlus />}
                bg={themeColor}
                color={themeTextColor}
                onClick={onCreateOpen}
                _hover={{ bg: "#e5c100" }}
              >
                Novo Usuário
              </Button>
            </HStack>
          </Flex>
        </CardBody>
      </Card>

      {/* Content Area */}
      {loading ? (
        <Card shadow="md" borderRadius="lg" bg={bgCard}>
          <CardBody>
            <Center py={10}>
              <VStack spacing={4}>
                <Spinner size="xl" thickness="4px" color={themeColor} />
                <Text color="black">Carregando usuários...</Text>
              </VStack>
            </Center>
          </CardBody>
        </Card>
      ) : users.length === 0 ? (
        <Card shadow="md" borderRadius="lg" bg={bgCard}>
          <CardBody>
            <Alert status="info" borderRadius="md" variant="subtle">
              <AlertIcon />
              Nenhum usuário encontrado. Adicione seu primeiro usuário usando o botão acima.
            </Alert>
          </CardBody>
        </Card>
      ) : (
        <Card shadow="md" borderRadius="lg" overflow="hidden" bg={bgCard}>
          <Box overflowX="auto">
            <Table colorScheme="yellow" size="md">
              <Thead bg={themeColor}>
                <Tr>
                  <Th color={themeTextColor}>Nome de Usuário</Th>
                  <Th color={themeTextColor}>E-mail</Th>
                  <Th color={themeTextColor}>Permissão</Th>
                  <Th width="150px" textAlign="center" color={themeTextColor}>Ações</Th>
                </Tr>
              </Thead>
              <Tbody>
                {users.map((user) => (
                  <Tr key={user._id} _hover={{ bg: 'gray.50' }} transition="all 0.2s">
                    <Td fontWeight="medium" color="black">{user.username}</Td>
                    <Td color="black">{user.email}</Td>
                    <Td>
                      <Badge 
                        colorScheme={user.permissions?.[0] === 'Master' ? 'red' : 'blue'} 
                        borderRadius="full" 
                        px={2} 
                        py={0.5}
                      >
                        {Array.isArray(user.permissions)
                          ? user.permissions[0]
                          : user.permissions || 'Suporte'}
                      </Badge>
                    </Td>
                    <Td>
                      <HStack spacing={2} justify="center">
                        <Button
                          leftIcon={<EditIcon />}
                          size="sm"
                          bg={themeColor}
                          color={themeTextColor}
                          onClick={() => handleEditClick(user)}
                          _hover={{ bg: "#e5c100" }}
                        >
                          Editar
                        </Button>
                        <Button
                          leftIcon={<LockIcon />}
                          size="sm"
                          bg="blue.500"
                          color="white"
                          onClick={() => handlePasswordReset(user)}
                          _hover={{ bg: "blue.600" }}
                        >
                          Senha
                        </Button>
                        <Button
                          leftIcon={<DeleteIcon />}
                          size="sm"
                          bg="red.500"
                          color="white"
                          onClick={() => handleDeleteClick(user)}
                          _hover={{ bg: "red.600" }}
                        >
                          Excluir
                        </Button>
                      </HStack>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </Box>
        </Card>
      )}

      {/* Modal de Edição */}
      <Modal isOpen={isOpen} onClose={onClose} size="md">
        <ModalOverlay backdropFilter="blur(5px)" />
        <ModalContent borderRadius="lg" bg={bgCard}>
          <Box bg={themeColor} h="8px" w="100%" />
          <ModalHeader color="black">Editar Usuário</ModalHeader>
          <ModalCloseButton color="black" />
          <ModalBody py={6}>
            {editUser && (
              <VStack spacing={5} align="stretch">
                <FormControl>
                  <FormLabel fontWeight="medium" color="black">Nome de Usuário</FormLabel>
                  <Input
                    value={editUser.username}
                    onChange={(e) =>
                      setEditUser({ ...editUser, username: e.target.value })
                    }
                    focusBorderColor={themeColor}
                    bg="white"
                  />
                </FormControl>
                <FormControl>
                  <FormLabel fontWeight="medium" color="black">E-mail</FormLabel>
                  <Input
                    value={editUser.email}
                    onChange={(e) =>
                      setEditUser({ ...editUser, email: e.target.value })
                    }
                    focusBorderColor={themeColor}
                    bg="white"
                  />
                </FormControl>
                <FormControl>
                  <FormLabel fontWeight="medium" color="black">Função</FormLabel>
                  <Select
                    value={editUser.permissions}
                    onChange={(e) =>
                      setEditUser({ ...editUser, permissions: e.target.value })
                    }
                    focusBorderColor={themeColor}
                    bg="white"
                  >
                    <option value="Master">Master</option>
                    <option value="Administração">Administração</option>
                    <option value="Marketing">Marketing</option>
                    <option value="Design">Design</option>
                    <option value="Financeiro">Financeiro</option>
                    <option value="Suporte">Suporte</option>
                    <option value="Comercial">Comercial</option>
                    <option value="TI">TI</option>
                  </Select>
                </FormControl>
                
                {/* Password Change Toggle */}
                <FormControl display="flex" alignItems="center">
                  <Button 
                    size="sm"
                    colorScheme={changePassword ? "red" : "blue"}
                    onClick={() => setChangePassword(!changePassword)}
                    mr={3}
                  >
                    {changePassword ? "Cancelar alteração de senha" : "Alterar senha"}
                  </Button>
                </FormControl>
                
                {/* New Password Field - Only shown when changePassword is true */}
                {changePassword && (
                  <FormControl>
                    <FormLabel fontWeight="medium" color="black">Nova Senha</FormLabel>
                    <InputGroup>
                      <Input
                        type={showPassword ? "text" : "password"}
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        focusBorderColor={themeColor}
                        bg="white"
                        pr="4.5rem"
                      />
                      <InputRightElement width="4.5rem">
                        <IconButton
                          h="1.75rem"
                          size="sm"
                          onClick={togglePasswordVisibility}
                          icon={showPassword ? <ViewOffIcon /> : <ViewIcon />}
                          aria-label={showPassword ? "Esconder senha" : "Mostrar senha"}
                        />
                      </InputRightElement>
                    </InputGroup>
                  </FormControl>
                )}
              </VStack>
            )}
          </ModalBody>
          <ModalFooter bg="gray.50" borderBottomRadius="lg">
            <Button variant="ghost" mr={3} onClick={onClose}>
              Cancelar
            </Button>
            <Button
              bg={themeColor}
              color={themeTextColor}
              onClick={handleUpdate}
              isLoading={loading}
              _hover={{ bg: "#e5c100" }}
            >
              Salvar
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Modal de Criação de Usuário */}
      <Modal isOpen={isCreateOpen} onClose={onCreateClose} size="md">
        <ModalOverlay backdropFilter="blur(5px)" />
        <ModalContent borderRadius="lg" bg={bgCard}>
          <Box bg={themeColor} h="8px" w="100%" />
          <ModalHeader color="black">Criar Novo Usuário</ModalHeader>
          <ModalCloseButton color="black" />
          <ModalBody py={6}>
            <VStack spacing={5} align="stretch">
              <FormControl isRequired>
                <FormLabel fontWeight="medium" color="black">Nome de Usuário</FormLabel>
                <Input
                  value={newUser.username}
                  onChange={(e) =>
                    setNewUser({ ...newUser, username: e.target.value })
                  }
                  focusBorderColor={themeColor}
                  bg="white"
                />
              </FormControl>
              <FormControl isRequired>
                <FormLabel fontWeight="medium" color="black">E-mail</FormLabel>
                <Input
                  type="email"
                  value={newUser.email}
                  onChange={(e) =>
                    setNewUser({ ...newUser, email: e.target.value })
                  }
                  focusBorderColor={themeColor}
                  bg="white"
                />
              </FormControl>
              <FormControl isRequired>
                <FormLabel fontWeight="medium" color="black">Senha</FormLabel>
                <InputGroup>
                  <Input
                    type={showPassword ? "text" : "password"}
                    value={newUser.password}
                    onChange={(e) =>
                      setNewUser({ ...newUser, password: e.target.value })
                    }
                    focusBorderColor={themeColor}
                    bg="white"
                    pr="4.5rem"
                  />
                  <InputRightElement width="4.5rem">
                    <IconButton
                      h="1.75rem"
                      size="sm"
                      onClick={togglePasswordVisibility}
                      icon={showPassword ? <ViewOffIcon /> : <ViewIcon />}
                      aria-label={showPassword ? "Esconder senha" : "Mostrar senha"}
                    />
                  </InputRightElement>
                </InputGroup>
              </FormControl>
              <FormControl>
                <FormLabel fontWeight="medium" color="black">Função</FormLabel>
                <Select
                  value={newUser.permissions}
                  onChange={(e) =>
                    setNewUser({ ...newUser, permissions: e.target.value })
                  }
                  focusBorderColor={themeColor}
                  bg="white"
                >
                  <option value="Master">Master</option>
                  <option value="Administração">Administração</option>
                  <option value="Marketing">Marketing</option>
                  <option value="Design">Design</option>
                  <option value="Financeiro">Financeiro</option>
                  <option value="Suporte">Suporte</option>
                  <option value="Comercial">Comercial</option>
                  <option value="TI">TI</option>
                </Select>
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter bg="gray.50" borderBottomRadius="lg">
            <Button variant="ghost" mr={3} onClick={onCreateClose}>
              Cancelar
            </Button>
            <Button
              bg={themeColor}
              color={themeTextColor}
              onClick={handleCreateUser}
              isLoading={loading}
              _hover={{ bg: "#e5c100" }}
            >
              Criar
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Confirm Delete Modal */}
      <Modal isOpen={isDeleteConfirmOpen} onClose={onDeleteConfirmClose} isCentered size="sm">
        <ModalOverlay backdropFilter="blur(5px)" />
        <ModalContent borderRadius="lg" bg={bgCard}>
          <Box bg="red.500" h="8px" w="100%" />
          <ModalHeader color="black">Confirmar Exclusão</ModalHeader>
          <ModalCloseButton color="black" />
          <ModalBody py={6}>
            {userToDelete && (
              <Text color="black">
                Tem certeza que deseja excluir o usuário <b>{userToDelete.username}</b>? Esta ação não poderá ser desfeita.
              </Text>
            )}
          </ModalBody>
          <ModalFooter bg="gray.50" borderBottomRadius="lg">
            <Button variant="outline" mr={3} onClick={onDeleteConfirmClose}>
              Cancelar
            </Button>
            <Button
              colorScheme="red"
              onClick={deleteUser}
              isLoading={loading}
            >
              Excluir
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Password Reset Modal */}
      {userToResetPassword && (
        <AdminPasswordResetModal
          isOpen={isPasswordResetOpen}
          onClose={() => {
            setIsPasswordResetOpen(false);
            setUserToResetPassword(null);
          }}
          userType="webuser"
          userId={userToResetPassword._id}
          userName={userToResetPassword.username}
          hasFastPassword={false}
          onSuccess={() => {
            toast({
              title: 'Sucesso',
              description: 'Senha redefinida com sucesso',
              status: 'success',
              duration: 3000,
              isClosable: true,
            });
            fetchUsers();
          }}
        />
      )}
    </Container>
  );
};

export default UserList;